/**
 * U&EYE cart panel — the single module that owns the cart → checkout handoff.
 *
 * Responsibilities:
 *  - validate the delivery form (Egyptian phone rules, tolerant formatting)
 *  - save delivery cart attributes additively via /cart/update.js
 *  - build the documented prefilled cart-permalink checkout URL from the cart
 *    response; when a cart can't be rebuilt losslessly, preserve it and use
 *    Shopify's existing-cart checkout route with the same prefill as a
 *    best-effort fallback
 *  - navigate via window.top so Instagram/Facebook in-app browsers escape
 *  - re-render itself through the Section Rendering API on cart updates
 *
 * No fetch monkey-patching, no MutationObservers, no checkout DOM access.
 */

if (!customElements.get('ueye-cart-panel')) {
  customElements.define(
    'ueye-cart-panel',
    class UeyeCartPanel extends HTMLElement {
      connectedCallback() {
        this.checkoutButton = this.querySelector('[data-ueye-checkout]');
        this.errorBox = this.querySelector('[data-ueye-error]');
        this.strings = this.readStrings();
        this.busy = false;

        this.checkoutButton?.addEventListener('click', () => this.onCheckout());
        this.querySelectorAll('[data-ueye-field]').forEach((field) => {
          field.addEventListener('input', () => {
            field.classList.remove('ueye-field__input--error');
            if (this.errorBox) this.errorBox.textContent = '';
          });
        });

        window.addEventListener('pageshow', this.onPageShow);
        this.trySubscribe();
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        window.removeEventListener('pageshow', this.onPageShow);
      }

      onPageShow = () => {
        this.busy = false;
        this.setBusy(false);
      };

      /* ---------- section refresh on cart updates ---------- */

      trySubscribe() {
        // pubsub.js is a deferred script; retry once the DOM is ready if needed.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
          return;
        }
        document.addEventListener(
          'DOMContentLoaded',
          () => {
            if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && !this.unsubscribe) {
              this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
            }
          },
          { once: true }
        );
      }

      /**
       * Cart updates (drawer or this page's own line-item steppers) publish
       * cartUpdate on every quantity change. This refresh patches ONLY the
       * money-dependent region (offer banner / shipping bar / summary) — it never
       * touches the delivery form, so a customer mid-typing an address never
       * loses their input or cursor focus because they also nudged a
       * quantity stepper.
       */
      async refresh() {
        if (this.busy) return;
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return;

        try {
          // No Accept header: on /cart specifically, `Accept: application/json`
          // makes Shopify answer with the same payload as /cart.js (cart data,
          // no `sections` key at all) instead of the Section Rendering API's
          // { sectionId: html } map, so the fetch below would always silently
          // no-op and this panel would go stale after every cart change.
          const response = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(sectionId)}`);
          if (!response.ok) return;
          const sections = await response.json();
          const html = sections[sectionId];
          if (!html) return;

          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('ueye-cart-panel');
          if (!fresh) return;

          const freshMoney = fresh.querySelector('[data-ueye-money]');
          const liveMoney = this.querySelector('[data-ueye-money]');
          if (!freshMoney) {
            // The cart just became empty (its last item was removed): this
            // panel now renders nothing server-side. Reload for a fully
            // consistent empty-cart page rather than leaving a stale form.
            window.location.reload();
            return;
          }
          if (liveMoney) liveMoney.innerHTML = freshMoney.innerHTML;

          const freshItemCount = fresh.dataset.itemCount;
          if (freshItemCount !== undefined) this.dataset.itemCount = freshItemCount;
        } catch (error) {
          // A failed refresh only leaves slightly stale copy; never break the cart.
          console.warn('[U&EYE cart] panel refresh skipped:', error);
        }
      }

      /* ---------- helpers ---------- */

      readStrings() {
        try {
          return JSON.parse(this.querySelector('[data-ueye-strings]')?.textContent || '{}');
        } catch (error) {
          return {};
        }
      }

      field(name) {
        return this.querySelector(`[data-ueye-field="${name}"]`);
      }

      value(name) {
        return String(this.field(name)?.value || '').trim();
      }

      showError(fieldName, message) {
        const field = this.field(fieldName);
        field?.classList.add('ueye-field__input--error');
        field?.focus({ preventScroll: true });
        field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (this.errorBox) this.errorBox.textContent = message || this.strings.errorGeneric || '';
      }

      /**
       * Egyptian mobile numbers: 010/011/012/015 + 8 digits.
       * Accepts +20 / 0020 prefixes, spaces, dashes, dots and Arabic-Indic digits.
       * Returns { local: '01xxxxxxxxx', e164: '+201xxxxxxxxx' } or null.
       */
      normalizePhone(raw) {
        const western = String(raw || '').replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));
        let digits = western.replace(/\D/g, '');
        if (digits.startsWith('0020')) digits = digits.slice(4);
        else if (digits.startsWith('20') && digits.length === 12) digits = digits.slice(2);
        if (digits.length === 10 && digits.startsWith('1')) digits = `0${digits}`;
        if (/^01[0125]\d{8}$/.test(digits)) {
          return { local: digits, e164: `+20${digits.slice(1)}` };
        }
        return null;
      }

      validate() {
        const s = this.strings;
        if (this.value('name').length < 3) return this.showError('name', s.errorName), false;

        const phone = this.normalizePhone(this.value('phone'));
        if (!phone) return this.showError('phone', s.errorPhone), false;
        this.phone = phone;

        if (!this.value('governorate')) return this.showError('governorate', s.errorGovernorate), false;
        if (this.value('city').length < 2) return this.showError('city', s.errorCity), false;
        if (this.value('address').length < 5) return this.showError('address', s.errorAddress), false;

        const email = this.value('email');
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          return this.showError('email', s.errorEmail), false;
        }
        return true;
      }

      setBusy(busy) {
        if (!this.checkoutButton) return;
        this.checkoutButton.toggleAttribute('aria-busy', busy);
        this.checkoutButton.classList.toggle('ueye-cart-panel__checkout--busy', busy);
        if (busy) {
          this.checkoutButton.dataset.ueyeLabel = this.checkoutButton.textContent;
          this.checkoutButton.textContent = this.strings.saving || '…';
        } else if (this.checkoutButton.dataset.ueyeLabel) {
          this.checkoutButton.textContent = this.checkoutButton.dataset.ueyeLabel;
        }
      }

      /* ---------- the handoff ---------- */

      async onCheckout() {
        if (this.busy || this.checkoutButton?.disabled) return;
        if (!this.validate()) return;

        this.busy = true;
        this.setBusy(true);

        const governorateSelect = this.field('governorate');
        const selectedOption = governorateSelect.options[governorateSelect.selectedIndex];
        const provinceEn = governorateSelect.value; // exact Shopify province name
        const governorateLabel = (selectedOption?.textContent || provinceEn).trim();
        const email = this.value('email');

        // /cart/update.js only touches the keys sent here, preserving other cart attributes.
        const attributes = {
          'Customer Name': this.value('name'),
          'Phone': this.phone.local,
          'Governorate': governorateLabel,
          'City': this.value('city'),
          'Address': this.value('address'),
          'Country': 'Egypt',
          'Checkout Details Completed': 'Yes',
        };
        if (email) attributes['Email'] = email;

        const root = window.Shopify?.routes?.root || '/';
        let cart = null;
        try {
          const response = await fetch(`${root}cart/update.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes }),
          });
          if (!response.ok) throw new Error(`cart/update.js ${response.status}`);
          cart = await response.json();
          if (cart?.errors) throw new Error(String(cart.errors));
        } catch (error) {
          console.error('[U&EYE cart] attribute save failed:', error);
          this.busy = false;
          this.setBusy(false);
          if (this.errorBox) this.errorBox.textContent = this.strings.errorGeneric || '';
          return;
        }

        this.navigate(
          this.buildCheckoutUrl(cart, {
            name: this.value('name'),
            phone: this.phone,
            provinceEn,
            city: this.value('city'),
            address: this.value('address'),
            email,
          })
        );
      }

      /**
       * Cart permalink: /cart/VARIANT:QTY,…?checkout[...]=… rebuilds the cart at
       * checkout entry with the address prefilled. Arbitrary properties,
       * selling plans and nested/component relationships can't be represented
       * losslessly. In those cases, retain the existing cart and use
       * /cart/checkout with the same prefill parameters as a best-effort
       * fallback. Shopify documents prefill on cart permalinks, not on the
       * existing-cart route, so every such fallback is deliberately loud.
       */
      buildCheckoutUrl(cart, details) {
        const root = window.Shopify?.routes?.root || '/';
        const base = root.endsWith('/') ? root : `${root}/`;

        const existingCartCheckout = (reason, diagnostic) => {
          const target = new URL(`${base}cart/checkout`, window.location.origin);
          this.appendCheckoutPrefill(target.searchParams, details);
          console.warn(
            '[U&EYE cart] documented cart-permalink reconstruction unavailable; preserving the existing cart and attempting prefill through /cart/checkout. Shopify does not document prefill on this fallback route, so it must be verified live:',
            { reason, ...diagnostic }
          );
          return target.toString();
        };

        const items = Array.isArray(cart?.items) ? cart.items : [];
        if (!items.length) return existingCartCheckout('cart response contained no line items', {});

        const unsupportedFeatures = (item) => {
          const properties = item?.properties || {};
          const parentRelationship = item?.parent_relationship;
          const hasParentRelationship =
            parentRelationship !== null &&
            parentRelationship !== undefined &&
            (typeof parentRelationship !== 'object' ||
              Object.values(parentRelationship).some(
                (value) => value !== null && value !== undefined && String(value) !== ''
              ));

          return {
            hasProperties: Object.values(properties).some(
              (value) => value !== null && value !== undefined && String(value) !== ''
            ),
            hasSellingPlan: Boolean(item?.selling_plan_allocation || item?.selling_plan),
            hasParentRelationship,
            hasComponents: [item?.item_components, item?.components].some((components) =>
              Array.isArray(components) ? components.length > 0 : Boolean(components)
            ),
          };
        };

        const unsupportedItem = items.find((item) => {
          const features = unsupportedFeatures(item);
          return Object.values(features).some(Boolean);
        });
        if (unsupportedItem) {
          return existingCartCheckout('line item has semantics a variant:quantity permalink cannot preserve', {
            itemKey: unsupportedItem?.key,
            variantId: unsupportedItem?.variant_id,
            ...unsupportedFeatures(unsupportedItem),
          });
        }

        const quantities = new Map();
        const invalidItem = items.find((item) => {
          const rawId = item?.variant_id;
          const id = String(rawId || '');
          const qty = Number(item?.quantity);
          const unsafeNumericId = typeof rawId === 'number' && !Number.isSafeInteger(rawId);
          return unsafeNumericId || !/^[1-9]\d*$/.test(id) || !Number.isSafeInteger(qty) || qty <= 0;
        });
        if (invalidItem) {
          return existingCartCheckout('line item has an invalid or non-numeric variant/quantity', {
            itemKey: invalidItem?.key,
            variantId: invalidItem?.variant_id,
            quantity: invalidItem?.quantity,
          });
        }

        items.forEach((item) => {
          const id = String(item.variant_id);
          const qty = Number(item.quantity);
          quantities.set(id, (quantities.get(id) || 0) + qty);
        });
        const lineParts = [...quantities.entries()].map(([id, qty]) => `${id}:${qty}`);
        if (!lineParts.length) {
          return existingCartCheckout('cart response contained no representable line items', {});
        }

        const target = new URL(`${base}cart/${lineParts.join(',')}`, window.location.origin);
        const params = target.searchParams;

        this.appendCheckoutPrefill(params, details);

        if (cart.note) params.set('note', String(cart.note));
        Object.entries(cart.attributes || {}).forEach(([key, value]) => {
          if (value === null || value === undefined || String(value) === '') return;
          params.set(`attributes[${key}]`, String(value));
        });

        const codes = (Array.isArray(cart.discount_codes) ? cart.discount_codes : [])
          .map((d) => (typeof d === 'string' ? d : d?.code))
          .filter(Boolean);
        if (codes.length) params.set('discount', codes.join(','));

        return target.toString();
      }

      /**
       * Checkout's prefill silently strips literal newlines from a
       * shipping-address field with no separator inserted, fusing adjacent
       * words together (confirmed live: "…جولف\nالتجمع…" arrives as
       * "…جولفالتجمع…"). The delivery textarea is multi-line and real
       * customers routinely address it that way, so collapse line breaks to
       * ", " here — before the value ever leaves the theme — rather than
       * relying on checkout to preserve them. Cart attributes (read by
       * ops/Vrobo) are untouched: they keep the raw newlines this collapses
       * only for the checkout URL param.
       */
      normalizeAddressLine(raw) {
        return String(raw || '')
          .split(/\r\n|\r|\n/)
          .map((line) => line.trim())
          .filter(Boolean)
          .join(', ')
          .replace(/\s{2,}/g, ' ');
      }

      appendCheckoutPrefill(params, details) {
        const nameParts = details.name.split(/\s+/).filter(Boolean);
        const firstName = nameParts.shift() || '';
        const lastName = nameParts.join(' ');

        if (firstName) params.set('checkout[shipping_address][first_name]', firstName);
        if (lastName) params.set('checkout[shipping_address][last_name]', lastName);
        params.set('checkout[shipping_address][address1]', this.normalizeAddressLine(details.address));
        params.set('checkout[shipping_address][city]', this.normalizeAddressLine(details.city));
        params.set('checkout[shipping_address][country]', 'Egypt');
        params.set('checkout[shipping_address][province]', details.provinceEn);
        params.set('checkout[shipping_address][phone]', details.phone.e164);
        params.set('checkout[phone]', details.phone.e164);
        if (details.email) params.set('checkout[email]', details.email);
      }

      navigate(url) {
        try {
          if (window.top && window.top !== window) {
            // A blocked top navigation that throws falls through to the current
            // frame below. Do not schedule a second navigation: this URL mutates
            // the cart, so concurrent top/frame requests can race each other.
            window.top.location.assign(url);
            return;
          }
          window.location.assign(url);
        } catch (error) {
          window.location.href = url;
        }
      }
    }
  );
}
